import React, { memo, useEffect, useRef } from 'react';
import { IconButton } from 'new/elements/IconButton/IconButton';
import classes from '../FlowNodes.module.scss';
import { ReactComponent as MinusIcon } from 'new/components/icons/fi-sr-minus-small.svg';
import { ReactComponent as PlusIcon } from 'new/components/icons/fi-sr-plus-small.svg';
import { ReactComponent as FolderIcon } from 'new/components/icons/folder.svg';
import { ReactComponent as RefreshIcon } from 'new/components/icons/fi-sr-refresh.svg';
import Typography from 'new/elements/Typography';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import FlowTypeIcon from 'js/components/pipelines/cards/FlowTypeIcon';
import PipelineStatusChip from 'new/components/pipelines/PipelineStatusChip/PipelineStatusChip';
import DataFlowDetailActionsContainer from 'js/components/data_flows/DataFlowDetailActionsContainer';
import { Handle, Position, useStore } from '@xyflow/react';
import classnames from 'classnames';
import { getRelativeNodesBounds } from 'js/components/pipelines/FlowNodes/utils';
import styles from './FlowNode.module.scss';
import { Chip, Link, Stack } from '@mui/material';
import { useExpandedNodes } from 'js/components/pipelines/FlowNodes/context/ExpandContext';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import { useMetricsAdapter } from 'new/state/metrics/adapter/useMetricsAdapter';
import { createSelector } from 'reselect';
import { useSelector } from 'react-redux';
import { NODE_HEIGHT_SMALL, NODE_HEIGHT } from 'js/components/pipelines/FlowNodes/consts';
import { FLOW_CANVAS_CONFIG, getDirectionConfig } from '../constants';
import { ConnectionLoader } from 'new/elements/ConnectionLoader/ConnectionLoader';
import { useResourceMeta } from 'new/hooks/adapters/useResourceMeta';
import { RESOURCE_TYPE_SOURCE } from 'js/constants/resourceTypes';
import { ConnectionTypeChip } from 'new/elements/ConnectionTypeChip/ConnectionTypeChip';
import { FlowCounters } from '../../cards/components/FlowCounters/FlowCounters';
import { RAGCounters } from '../../cards/components/RAGCounters/RAGCounters';

interface FlowItemProps {
  data: {
    selected: boolean;
    height: number;
    expanded?: boolean;
    hideToolbar?: (flow) => boolean;
    onFitViewItem?: (flowNodeId: number, fit: boolean) => boolean;
    width: number;
    item: any;
    linksMap?: any;
    toggleCloneFlowModal?: () => void;
    project?: any;
    layoutDirection?: 'LR' | 'TB';
  };
}

const pipelineRefreshingSelector = (flowNodeId) =>
  createSelector([(s: any) => s.pipelines.refreshing[flowNodeId]], (refreshing) => {
    return refreshing;
  });

export const FlowNode = (props: FlowItemProps) => {
  const { data } = props;
  const {
    height = 500,
    width = 500,
    onFitViewItem = () => {},
    expanded,
    item,
    hideToolbar = () => false,
    toggleCloneFlowModal,
    selected,
    project,
    layoutDirection = 'LR',
  } = data;

  const dirCfg = getDirectionConfig(layoutDirection);

  const idRefresh = useSelector(pipelineRefreshingSelector(item.flow_node_id));

  const { onExpandNode, expandedNodes } = useExpandedNodes();
  const { refreshPipelineById } = usePipelinesAdapter();
  const { requestFlowMetrics } = useMetricsAdapter();
  const hasAutoExpandedRef = useRef(false);

  useEffect(() => {
    // Auto-expand flow on mount if not already expanded
    // Use ref to ensure this only runs once per component instance
    if (!hasAutoExpandedRef.current && !expandedNodes.includes('flow-' + item.flow_node_id)) {
      hasAutoExpandedRef.current = true;
      onExpandNode('flow-' + item.flow_node_id);
    }
  }, [expandedNodes, onExpandNode, item.flow_node_id]);

  /**
   * ISSUE: Missing Flow Items When Expanding Flows
   *
   * Problem: When expanding flows, items might not be loaded yet, which causes:
   * - Edges can't be created (no items.children to create edges from)
   * - Nodes inside the flow don't appear
   * - The flow appears empty even though it has resources
   *
   * Solution: Automatically fetch flow definition when expanded if items are missing.
   * This ensures that when a user expands a flow, we have all the data needed to
   * display nodes and edges inside the flow.
   *
   * Note: For main flows, items should be loaded initially, so this is a fallback.
   * For linked flows, we always fetch on expand (handled in SecondaryFlowNode).
   */
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (expanded && !idRefresh && !hasFetchedRef.current) {
      const hasItems = item.items && Object.keys(item.items).length > 0;

      // Only fetch if items are missing - this ensures edges can be created
      // Main flows should have items loaded initially, so this is a fallback
      if (!hasItems) {
        hasFetchedRef.current = true;
        refreshPipelineById(item.flow_node_id);
      } else {
        hasFetchedRef.current = true;
      }
    } else if (!expanded) {
      // Reset fetch flag when collapsed so it can fetch again if needed
      hasFetchedRef.current = false;
    }
  }, [expanded, item.items, item.flow_node_id, refreshPipelineById, idRefresh]);

  const { project: flowProject, renderProjectLabel } = item;

  const containerCfg = FLOW_CANVAS_CONFIG.shared.flowContainer;

  const { minWidth, minHeight, hasChildNodes } = useStore((store) => {
    const childNodes = Array.from(store.nodeLookup.values()).filter(
      (n) => n.parentId === 'flow-' + item.flow_node_id,
    );
    const rect = getRelativeNodesBounds(childNodes);

    return {
      minWidth: rect.x + rect.width + containerCfg.paddingX,
      minHeight: rect.y + rect.height + NODE_HEIGHT_SMALL + containerCfg.paddingY,
      hasChildNodes: childNodes.length > 0,
    };
  }, isEqual);

  const meta = useResourceMeta(RESOURCE_TYPE_SOURCE, item.data_source_id, item.data_credentials);

  const flowId = item.flow_node_id || item.id;

  const hasLinks = item.linked_flows?.length || item.flow_triggers?.length;

  const handleExpand = () => {
    onExpandNode('flow-' + flowId);
  };

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    refreshPipelineById(flowId);
    requestFlowMetrics(flowId, item.data_source_id);
  };

  return (
    <div
      style={{
        position: 'relative',
      }}
      onClick={() => onFitViewItem(item, false)}
    >
      {hasLinks ? (
        <>
          <Handle isConnectable={false} id="target-flow" type="target" position={Position.Left} />
          <Handle isConnectable={false} id="source-flow" type="source" position={Position.Left} />
        </>
      ) : null}
      {item.flow_type !== 'rag' ? (
        <div
          onClick={(e) => {
            e.stopPropagation();
            handleExpand();
          }}
          className={classes.expandButton}
        >
          {expanded ? <MinusIcon /> : <PlusIcon />}
        </div>
      ) : null}
      {!hideToolbar(item) ? (
        <div
          className={classes.flowNodeToolbar}
          style={{
            zIndex: 2,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={classes.flowItemInfoContainer}>
            <Stack gap="8px" flex={1}>
              <Typography className={classes.flowItemName}>{item.name}</Typography>
              <Stack direction="row" gap="16px">
                <ConnectionTypeChip
                  icon={meta?.iconSm}
                  label={meta?.label}
                  style={{ border: 'none', padding: 0 }}
                />
                <ChipWithCopy tooltipTitle="Copy Flow ID" toCopy={flowId}>
                  ID {flowId}
                </ChipWithCopy>
                {flowProject && renderProjectLabel ? (
                  <Chip
                    data-not-expanding="true"
                    onClickCapture={(e) => e.stopPropagation()}
                    color="warning"
                    classes={{
                      label: styles.chipLabel,
                      root: styles.chipRootProject,
                    }}
                    label={
                      <>
                        <FolderIcon width="12px" height="11px" />
                        <Link href={`/integrate/projects/${flowProject.id}`} variant="body3">
                          {flowProject.name}
                        </Link>
                      </>
                    }
                  />
                ) : null}
                <FlowTypeIcon data={item} />
                <PipelineStatusChip
                  id={flowId}
                  status={item.status}
                  archivalStatus={item.archival_status}
                  showRuntimeIndicator={false}
                />
              </Stack>
              {item.flow_type === 'rag' ? (
                <RAGCounters data={item} isClickablePartHovered={false} hideArrow />
              ) : (
                <FlowCounters hideArrow isClickablePartHovered={false} source={item} />
              )}
            </Stack>
            <Stack direction="row" gap="4px" alignItems="center">
              <IconButton
                size="small"
                onClick={handleRefresh}
                title="Refresh flow and metrics"
                disabled={idRefresh}
              >
                <RefreshIcon style={{ width: '14px', height: '14px' }} />
              </IconButton>
              <DataFlowDetailActionsContainer
                data={item}
                projectId={project?.id || item.project_id}
                inPipeline
                toggleCloneFlowModal={toggleCloneFlowModal}
              />
            </Stack>
          </div>
        </div>
      ) : null}
      <div
        className={classnames(classes.nodesContainer, {
          [classes.nodesContainer_selected]: selected,
          [classes.nodesContainer_secondary]: (data as any)?.isProjectFlow,
        })}
        style={{
          height: expanded ? height - dirCfg.flowVerticalSpacing : 0,
          width: expanded ? width : 0,
          minHeight: expanded ? minHeight - dirCfg.flowVerticalSpacing + 30 : 0,
          minWidth: expanded ? minWidth : 0,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* TODO: Replace with localized loading indicator scoped to flow elements area
        {expanded && idRefresh && (
          <ConnectionLoader loading={true} message="Loading Flow" />
        )}
        */}
      </div>
    </div>
  );
};

type IsEqualCompareObj = {
  minWidth: number;
  minHeight: number;
  hasChildNodes: boolean;
};

function isEqual(prev: IsEqualCompareObj, next: IsEqualCompareObj): boolean {
  return (
    prev.minWidth === next.minWidth &&
    prev.minHeight === next.minHeight &&
    prev.hasChildNodes === next.hasChildNodes
  );
}

export default memo(FlowNode);
